import { useClientPortalSubmit } from "@certn/api";
import {
  Container,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
  ProgressLoader,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useCallback, useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { array, object, string } from "yup";

import {
  UnifiedIntegrationContext,
  AtsIntegration,
} from "../../UnifiedIntegrationContext";
import { UnifiedIntegrationStepLayout } from "../UnifiedIntegrationStepLayout";
import { WhatsThis } from "../WhatsThis";
import constants from "../../../../../../constants";
import { UNIFIED_WORKSPACE_ID } from "../../../../../../constants";

interface UnifiedIntegration {
  type: string;
  name: string;
  logo_url: string;
  description: string;
  token_names: string[];
  token_instructions: string[];
}

export function TokensStep() {
  const { "*": integrationType } = useParams();
  const { t } = useTranslation();
  const [authenticationError, setAuthenticationError] = useState(false);
  const {
    atsIntegrationData,
    setAtsIntegration,
    goToNextStep,
    setUnifiedConfig,
  } = useContext(UnifiedIntegrationContext);

  const validationSchema = object().shape({
    tokens: array().of(string()).required(),
  });

  const env = constants.MODE === "production" ? "Production" : "Staging";
  const { data: unifiedIntegrations } = useSWR<UnifiedIntegration[]>(
    `https://api.unified.to/unified/integration/workspace/${UNIFIED_WORKSPACE_ID}?env=${env}`,
    (url: string) => fetch(url).then((res) => res.json())
  );
  const integration = unifiedIntegrations?.find(
    (each) => each.type === integrationType
  );
  const integrationName = integration?.name;
  const tokenNames = integration?.token_names || ["API Token"];
  const fields = tokenNames.map((tokenName, idx) => ({
    name: tokenName,
    instructions: integration?.token_instructions?.[idx] || "",
  }));

  // "token_names": [
  //   "API Key",
  //   "Job board token"
  // ],
  // "token_instructions": [
  //   "API Keys can be found in the \"Configure >> Dev Center >> API Credential Management\" page.",
  //   "Comma delimited.  Optional, type a space in the input field if not required.  Documentation on how to find your job board tokens is <a target=\"_blank\" href=\"https://support.greenhouse.io/hc/en-us/articles/360020776251-Job-board-URL-for-Unified-hosted-job-board\">here</a>."
  // ],

  const { submit } = useClientPortalSubmit(
    "/api/internal/client-portal/ats-integrations/unified/create-connection/",
    {
      onFailure: () => {
        setAuthenticationError(true);
      },
      onSuccess: (unifiedConfig) => {
        setUnifiedConfig(unifiedConfig);
        goToNextStep();
      },
    }
  );

  const handleSubmit = useCallback(
    async (values: AtsIntegration) => {
      setAuthenticationError(false);
      setAtsIntegration(values);
      await submit({
        tokens: values.tokens,
        ats_type: integrationType! as any,
      });
    },
    [setAtsIntegration, integrationType, submit]
  );

  return (
    <Formik
      initialValues={atsIntegrationData}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, values }) => {
        return (
          <UnifiedIntegrationStepLayout
            onComplete={handleSubmit}
            disableCompletion={!isValid}
            submitting={isSubmitting}
          >
            <Container
              layout="narrow"
              testID="unified-details-page-outermost-container"
            >
              <Stack gap="space-2">
                <div>
                  <Text style="input/section-title">
                    {t("step {{stepNumber}} of {{stepCount}}", {
                      stepNumber: 1,
                      stepCount: 2,
                    })}
                  </Text>
                </div>
                <Text
                  as="h1"
                  className={styles.titleSpacing}
                  style="global/title-small"
                >
                  {t("{{integration}} integration", {
                    integration: integrationName,
                  })}
                </Text>
                <ProgressLoader floating value={50} />
                <Stack gap="space-2">
                  <Text style="global/heading-medium">
                    {t(
                      "Add your integration details here to set up the connection with Certn. These enable Certn to send information to {{integration}}.",
                      { integration: integrationName }
                    )}
                  </Text>
                </Stack>
                <div className={styles.mainContentContainer}>
                  {authenticationError && (
                    <SectionMessage
                      type="danger"
                      sectionTitle={t("Authentication failed")}
                      icon="error"
                    >
                      <Trans>
                        Please check your credentials and try again.
                      </Trans>
                    </SectionMessage>
                  )}
                  {fields.map(({ name, instructions }, idx) => (
                    <TextInputFormikField
                      key={idx}
                      name={`tokens[${idx}]`}
                      label={name}
                      testID={`api-token-${idx}`}
                      description={
                        (
                          <div
                            dangerouslySetInnerHTML={{ __html: instructions }}
                          />
                        ) as any
                      }
                      info={
                        <WhatsThis>
                          <div
                            dangerouslySetInnerHTML={{ __html: instructions }}
                          />
                        </WhatsThis>
                      }
                      isRequired
                      isDisabled={isSubmitting}
                    />
                  ))}
                </div>
              </Stack>
            </Container>
          </UnifiedIntegrationStepLayout>
        );
      }}
    </Formik>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
};
